<script setup lang="ts">
import { nextTick, ref } from "vue";
import { useAbility } from "@casl/vue";
import Dictionaries from "@/domains/home/services/configurations/views/dictionary/Dictionaries.vue";
import NameReservation from "@/domains/home/services/configurations/views/reservations/NameReservation.vue";
import NatureOfBusiness from "@/domains/home/services/configurations/views/nature_of_business/NatureOfBusiness.vue";
import QuickNameSearch from "@/domains/home/services/configurations/views/reservations/QuickNameSearch.vue";
import AGM from "@/domains/home/services/configurations/views/agm/Agm.vue";
import InsolvencySettings from "@/domains/home/services/configurations/views/settings/InsolvencySettings.vue";
import GeneralSettings from "@/domains/home/services/configurations/views/settings/GeneralSettings.vue";
import MobileAppConfigurations from "@/domains/home/services/configurations/views/mobile/MobileAppConfigurations.vue";
import GatewayTab from "@/domains/home/services/configurations/views/gateway/GatewayTab.vue";
import HelpDeskSettings from "@/domains/home/services/configurations/views/helpdesk/HelpDeskSettings.vue";
import UraTab from "@/domains/home/services/configurations/views/ura/UraTab.vue";
import SimpoTab from "@/domains/home/services/configurations/views/simpo/SimpoTab.vue";
import FeatureFlagsTab from "@/domains/home/services/configurations/views/featureFlags/FeatureFlagsTab.vue";

type TabRef = { refresh?: () => void } | null;

const { can } = useAbility();
const canViewMenuItem = (permission: string) => can(permission, "general") === true;

const currentRoute = ref<string>("name-search");
const gatewayRef = ref<TabRef>(null);
const uraRef = ref<TabRef>(null);
const simpoRef = ref<TabRef>(null);
const featureFlagsRef = ref<TabRef>(null);

const select = (route: string) => {
  if (route === "simpo" && !canViewMenuItem("can_modify_simpo_settings")) {
    return;
  }
  if (route === "feature-flags" && !canViewMenuItem("can_manage_feature_flags")) {
    return;
  }
  currentRoute.value = route;
  if (route === "gateway") {
    nextTick(() => gatewayRef.value?.refresh?.());
  } else if (route === "ura") {
    nextTick(() => uraRef.value?.refresh?.());
  } else if (route === "simpo") {
    nextTick(() => simpoRef.value?.refresh?.());
  } else if (route === "feature-flags") {
    nextTick(() => featureFlagsRef.value?.refresh?.());
  }
};
</script>

<template>
  <div class="flex flex-col bg-white rounded-sm text-gray p-3">

    <div class="border-b border-gray-200 mb-4">
      <nav class="flex flex-wrap gap-x-6 gap-y-0 px-4" aria-label="Tabs">
      <div
          :class="[(currentRoute === 'general') ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300', 'whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition cursor-pointer']"
          @click="select('general')">
        <div class="flex">
          <div>General</div>
        </div>
      </div>
      <div
          :class="[(currentRoute === 'dictionaries') ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300', 'whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition cursor-pointer']"
          @click="select('dictionaries')"
      >
        <div class="flex">
          <div>Dictionaries</div>
        </div>
      </div>
      <div
          :class="[(currentRoute === 'isic') ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300', 'whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition cursor-pointer']"
          @click="select('isic')"
      >
        <div class="flex">
          <div>ISIC (Nature of Business)</div>
        </div>
      </div>
      <div
          :class="[(currentRoute === 'reservation') ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300', 'whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition cursor-pointer']"
          @click="select('reservation')"
      >
        <div class="flex">
          <div>Name Reservation</div>
        </div>
      </div>
      <div
          :class="[(currentRoute === 'name-search') ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300', 'whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition cursor-pointer']"
          @click="select('name-search')"
      >
        <div class="flex">
          <div>Name Search</div>
        </div>
      </div>
      <div
          :class="[(currentRoute === 'insolvency') ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300', 'whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition cursor-pointer']"
          @click="select('insolvency')"
      >
        <div class="flex">
          <div>Insolvency</div>
        </div>
      </div>
      <div
          :class="[(currentRoute === 'mobile') ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300', 'whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition cursor-pointer']"
          @click="select('mobile')"
      >
        <div class="flex">
          <div>Mobile</div>
        </div>
      </div>
      <div
          :class="[(currentRoute === 'agm') ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300', 'whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition cursor-pointer']"
          @click="select('agm')"
      >
        <div class="flex">
          <div>Annual General Meeting</div>
        </div>
      </div>
      <div
          :class="[(currentRoute === 'gateway') ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300', 'whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition cursor-pointer']"
          @click="select('gateway')"
      >
        <div class="flex">
          <div>Gateway</div>
        </div>
      </div>
      <div
          :class="[(currentRoute === 'helpdesk') ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300', 'whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition cursor-pointer']"
          @click="select('helpdesk')"
      >
        <div class="flex">
          <div>Help Desk</div>
        </div>
      </div>
      <div
          :class="[(currentRoute === 'ura') ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300', 'whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition cursor-pointer']"
          @click="select('ura')"
      >
        <div class="flex">
          <div>Integration Config</div>
        </div>
      </div>
      <div
          v-if="canViewMenuItem('can_modify_simpo_settings')"
          :class="[(currentRoute === 'simpo') ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300', 'whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition cursor-pointer']"
          @click="select('simpo')"
      >
        <div class="flex">
          <div>SIMPO</div>
        </div>
      </div>
      <div
          v-if="canViewMenuItem('can_manage_feature_flags')"
          :class="[(currentRoute === 'feature-flags') ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300', 'whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 transition cursor-pointer']"
          @click="select('feature-flags')"
      >
        <div class="flex">
          <div>Feature Flags</div>
        </div>
      </div>
      </nav>
    </div>

    <div class="flex flex-col py-2">
      <GeneralSettings v-if="currentRoute === 'general'"/>
      <InsolvencySettings v-if="currentRoute === 'insolvency'" />
      <Dictionaries v-if="currentRoute === 'dictionaries'"/>
      <NameReservation v-if="currentRoute === 'reservation'"/>
      <NatureOfBusiness v-if="currentRoute === 'isic'"/>
      <QuickNameSearch v-if="currentRoute === 'name-search'"/>
      <MobileAppConfigurations v-if="currentRoute === 'mobile'"/>
      <AGM v-if="currentRoute === 'agm'"/>
      <GatewayTab v-if="currentRoute === 'gateway'" ref="gatewayRef" />
      <HelpDeskSettings v-if="currentRoute === 'helpdesk'"/>
      <UraTab v-if="currentRoute === 'ura'" ref="uraRef" />
      <SimpoTab
        v-if="currentRoute === 'simpo' && canViewMenuItem('can_modify_simpo_settings')"
        ref="simpoRef"
      />
      <FeatureFlagsTab
        v-if="currentRoute === 'feature-flags' && canViewMenuItem('can_manage_feature_flags')"
        ref="featureFlagsRef"
      />
    </div>
  </div>
</template>
